import PropTypes from 'prop-types';
import React, { Component } from 'react';
import _ from 'lodash';
import classNames from 'classnames';

import { Modal, ModalHeader, ModalContent } from 'common/components/Modal';
import SocrataIcon from 'common/components/SocrataIcon';
import { fetchJson, defaultHeaders } from 'common/http';
import AssetSelector from 'common/components/AssetSelector';
import * as assetSelectorConstants from 'common/components/AssetBrowser/lib/constants';
import { currentUserHasRight } from 'common/current_user';
import { getCurrentDomain } from 'common/currentDomain';
import { FeatureFlags } from 'common/feature_flags';

import { accessPointFor, getView } from 'common/usaid_collections/associateCollections';

import './index.scss';

/**
 * EN-19924: USAID special feature
 */

// Limit of how many associated assets can be added. This will probably become a prop once we allow
// more than 1 (for the case where we're on a parent selecting multiple children).
const ASSET_LIMIT = 1;

export class AssociatedAssets extends Component {
  constructor(props) {
    super(props);

    this.state = {
      assetSelectorIsOpen: false,
      selectedAssets: []
    };
  }

  componentDidMount() {
    const { isDraft } = this.props;

    const viewEndpoint = isDraft ?
      `/api/publishing/v1/revision/${this.props.uid}/0` : `/api/views/${this.props.uid}`;
    // Fetch existing associated assets. This should probably not be in this file
    fetchJson(viewEndpoint, {
      credentials: 'same-origin',
      headers: defaultHeaders
    }).then(response => {
      const collectionParentUid = isDraft ?
        _.get(response, 'resource.metadata.metadata.collectionParent') :
        _.get(response, 'metadata.collectionParent');

      if (collectionParentUid) {
        getView(collectionParentUid).then(parentView => {
          const collectionParentAccessPoint = accessPointFor(parentView);

          const { uid, title, urls } = collectionParentAccessPoint;
          const url = _.first(Object.values(urls));
          this.setState({ selectedAssets: [{ id: uid, name: title, url }] });
        }).catch(err => {
          console.error(`failed to fetch collectionParent ${collectionParentUid}`, err);
        });
      }
    })
    .catch(err => {
      console.error('failed to fetch associated assets', err);
    });
  }

  handleRemoveAssetClick(event, removedAssetId) {
    event.preventDefault();
    const newSelectedAssets = this.state.selectedAssets.filter((assets) => assets.id !== removedAssetId);
    this.setState({ selectedAssets: newSelectedAssets });
  }

  renderAssociatedAssetLinks() {
    const assetLinks = _.map(this.state.selectedAssets, (asset) => (
      <li className="associated-asset-link" key={asset.id}>
        <a
          href="#"
          title="Remove"
          onClick={(event) => this.handleRemoveAssetClick(event, asset.id)}
          className="remove-asset">
          <SocrataIcon name="close-2" />
        </a>
        <a href={asset.url} target="_blank" className="btn btn-xs btn-primary name" rel="noreferrer">{asset.name}</a>
        <p className="description">{asset.description}</p>
      </li>
    ));

    return (
      <ul className="associated-asset-links">
        {assetLinks}
      </ul>
    );
  }

  renderAddAssociatedAssetButton() {
    if (this.state.selectedAssets.length >= ASSET_LIMIT) return null;

    const handleClick = (event) => {
      event.preventDefault();
      this.setState({ assetSelectorIsOpen: true });
    };

    return (
      <a
        href="#"
        className="btn btn-sm btn-default"
        onClick={handleClick}>
        Add
      </a>
    );
  }

  render() {
    const { apiCalls, modalIsOpen, onDismiss, onSave } = this.props;
    const { assetSelectorIsOpen, selectedAssets } = this.state;

    const modalProps = {
      className: 'associated-assets',
      fullScreen: false,
      onDismiss,
      overlay: true
    };

    const handleAssetSelectorClose = () => {
      this.setState({ assetSelectorIsOpen: false });
    };

    // Columns to use for the "list view" asset selector.
    const assetSelectorColumns = [
      assetSelectorConstants.COLUMN_TYPE,
      assetSelectorConstants.COLUMN_NAME,
      assetSelectorConstants.COLUMN_LAST_UPDATED_DATE,
      assetSelectorConstants.COLUMN_OWNER,
      assetSelectorConstants.COLUMN_AUDIENCE,
      assetSelectorConstants.COLUMN_ACTIONS
    ];

  const assetSelectorProps = {
      assetSelector: true,
      baseFilters: {
        assetTypes: 'href',
        collectionParent: true,
        forUser: _.get(window, 'socrata.currentUser.id'),
        published: true,
        source: getCurrentDomain()
      },
      columns: assetSelectorColumns,
      onAssetSelected: (assetData) => {
        this.setState({ selectedAssets: selectedAssets.concat(assetData) });
      },
      onClose: handleAssetSelectorClose,
      resultsPerPage: 6,
      title: 'Add to Existing Data Asset'
    };

    const handleCancel = (e) => {
      e.preventDefault();
      onDismiss();
    };

    const handleSave = (e) => {
      e.preventDefault();
      onSave(selectedAssets);
    };

    const lastApiCall = _.last(apiCalls) || {};
    const lastApiCallStatus = lastApiCall.status;
    const saveInProgress = lastApiCallStatus === 'STATUS_CALL_IN_PROGRESS';
    const saveSuccess = lastApiCallStatus === 'STATUS_CALL_SUCCEEDED';
    const saveFailure = lastApiCallStatus === 'STATUS_CALL_FAILED';

    const saveButtonIsDisabled = selectedAssets.length < 1;

    const saveButtonClasses = classNames('btn btn-sm btn-primary save-button', {
      'btn-success': saveSuccess,
      'btn-error': saveFailure,
      'btn-disabled': saveButtonIsDisabled
    });

    const loadingSpinner = saveInProgress && (
      <span className="spinner-default" />
    );

    const errorMessage = saveFailure && (
      <p className="alert error">
        We are sorry, there was an error saving your association.
        Please try again or contact support@socrata.com
      </p>
    );

    // This is a terrible way to do this. If the api call succeeded in the last 500ms, close the modal.
    // This is to prevent the modal from closing again if the user re-opens it after a success.
    const saveJustSucceeded = saveSuccess && Date.now().valueOf() - lastApiCall.succeededAt.valueOf() < 500;
    if (saveJustSucceeded) {
      setTimeout(onDismiss, 1000);
    }

    const modal = modalIsOpen && (
      <Modal {...modalProps}>
        <ModalHeader title={'Associate with Parent Asset'} onDismiss={onDismiss} />
        <ModalContent>
          <p className="association-note">{`Note: In order to associate your dataset with a data asset,
            the data asset must have been submitted for review. You cannot associate a dataset with a draft
            data asset.`}</p>
          {this.renderAssociatedAssetLinks()}
          {this.renderAddAssociatedAssetButton()}
          <div className="action-buttons">
            <a href="#" className="btn btn-sm btn-default cancel-button" onClick={handleCancel}>Cancel</a>
            <a
              href="#"
              disabled={saveButtonIsDisabled}
              className={saveButtonClasses}
              onClick={handleSave}>
              Save
              {loadingSpinner}
            </a>
          </div>
          {errorMessage}
        </ModalContent>
      </Modal>
    );

    return (
      <div>
        {modal}
        {assetSelectorIsOpen && <AssetSelector {...assetSelectorProps} />}
      </div>
    );
  }
}

AssociatedAssets.propTypes = {
  apiCalls: PropTypes.array,
  isDraft: PropTypes.bool.isRequired,
  modalIsOpen: PropTypes.bool,
  onDismiss: PropTypes.func,
  onSave: PropTypes.func.isRequired,
  uid: PropTypes.string.isRequired
};

AssociatedAssets.defaultProps = {
  apiCalls: [],
  modalIsOpen: true,
  onDismiss: _.noop
};

export default AssociatedAssets;
